/**
 * Aba Promoções — cards de campanha (Mercado Turbo) que filtram a tabela; barra de status
 * Todos/Candidatos/Pendentes/Participantes; busca por título, MLB ou SKU; seleção múltipla com
 * ações em massa (Participar / Sair das selecionadas); linha por anúncio com preço cheio + M.C. (e M.C. hoje), quem banca, preço
 * final e M.C. final. Clicar na linha abre o anúncio na aba "Por anúncio".
 */
import { useMemo, useState } from 'react'
import { Box, Button, Checkbox, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, useTheme } from '@mui/material'
import {
  chaveCampanha, TIPOS_SEM_POST, MOTIVO_SEM_POST, campanhasDaConta, mcDe, promoRange, rangeToPrice, selKey, textoSelecao, type Anuncio, type Promocao, type StatusPromo } from './logic'
import { usePromo } from './ctx'
import { AcaoCell, AdCell, BRAND, McFinalCell, OrigCell, P, Panel, Pill, PrecoPromoCell, SeloAcao, SplitCell, ddmm, dias, useArmado } from './ui'

const PAGE = 120
/** Cabeçalhos longos quebram linha dentro de uma largura fixa, em vez de esticar a coluna
 *  (pedido de 13/10/2026: a tabela precisa caber na tela sem barra de rolagem horizontal). */
const thEstreito = { whiteSpace: 'normal !important', width: 150, maxWidth: 150, lineHeight: 1.25 } as const
type Filtro = 'todos' | StatusPromo
const STATUS: Array<[Filtro, string]> = [['todos', 'Todos'], ['candidate', 'Candidatos'], ['pending', 'Pendentes'], ['started', 'Participantes']]

export function AbaPromocoes() {
  const ctx = usePromo()
  const { conta } = ctx
  const t = useTheme()
  const [campSel, setCampSel] = useState<string | null>(null)
  const [status, setStatus] = useState<Filtro>('todos')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [bulk, setBulk] = useState<string | null>(null)
  const [prog, setProg] = useState<string | null>(null)
  const [armado, armar, desarmar] = useArmado(6000)
  const [armadoSair, armarSair, desarmarSair] = useArmado(6000)
  const [verEncerradas, setVerEncerradas] = useState(false)
  // 02/10/2026 — os cards saem do status ATUAL das promoções (mesma fonte da tabela), não do agregado
  // da coleta das 02:30: depois da releitura da API e do robô, os dois números divergiam.
  const campanhas = useMemo(() => campanhasDaConta(conta), [conta])
  const encerradas = campanhas.filter((c) => c.encerrada)
  const cards = verEncerradas ? campanhas : campanhas.filter((c) => !c.encerrada || c.n_ativa > 0)

  const promosFiltradas = (a: Anuncio) => a.promocoes.filter((p) => (!campSel || chaveCampanha(p) === campSel) && (status === 'todos' || p.status === status))
  const promoParaExibir = (a: Anuncio): Promocao | null => {
    const pool = promosFiltradas(a)
    if (campSel || status !== 'todos') return pool[0] ?? null
    return a.promocoes.find((p) => p.id === a.melhor_promo_id) ?? pool[0] ?? null
  }

  const counts = useMemo(() => {
    const c = { todos: 0, candidate: 0, pending: 0, started: 0, finished: 0 }
    for (const a of conta.ads) for (const p of a.promocoes) {
      if (campSel && chaveCampanha(p) !== campSel) continue
      c.todos++
      if (p.status in c) c[p.status]++
    }
    return c
  }, [conta, campSel])

  const linhas = useMemo(() => {
    const term = q.trim().toLowerCase()
    return conta.ads.filter((a) => {
      if (term && !`${a.titulo} ${a.mlb} ${a.sku ?? ''}`.toLowerCase().includes(term)) return false
      if (campSel || status !== 'todos') return promosFiltradas(a).length > 0
      return true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conta, q, campSel, status])
  const visiveis = linhas.slice(0, page * PAGE)
  const camp = campSel ? campanhas.find((c) => c.key === campSel) : null

  const todosPagina = visiveis.every((a) => { const p = promoParaExibir(a); return !p || ctx.sel.has(selKey(a, p)) })
  const marcarPagina = (on: boolean) => { for (const a of visiveis) { const p = promoParaExibir(a); if (p) ctx.toggleSel(selKey(a, p), on) } }

  /** Ações múltiplas: em preview marca; com o serviço, dois cliques e uma chamada por linha. */
  const participarSelecionadas = async () => {
    const alvos: Array<{ a: Anuncio; p: Promocao }> = []
    for (const a of conta.ads) for (const p of a.promocoes) if (ctx.sel.has(selKey(a, p)) && p.status !== 'started') alvos.push({ a, p })
    if (!alvos.length) { setProg('Nenhuma linha marcada. Marque as caixinhas das promoções que quer ativar e clique de novo.'); return }
    if (!ctx.escreve) {
      ctx.marcarLote(alvos.map(({ a, p }) => ({ k: selKey(a, p), status: 'started' as const, preview: true })))
      setProg(`${alvos.length} marcadas em preview (nada enviado ao ML)`)
      return
    }
    if (!armado) { armar(); return }
    desarmar()
    let ok = 0; let semPost = 0; const erros: string[] = []
    for (let i = 0; i < alvos.length; i++) {
      const { a, p } = alvos[i]
      setProg(`${i + 1}/${alvos.length} — ${a.mlb}…`)
      if (TIPOS_SEM_POST.has(p.type)) { semPost++; continue }           // 21/10/2026: Relâmpago Smart sem POST documentado
      const range = promoRange(p)
      const v = ctx.pickerVal.get(selKey(a, p))
      const preco = range && v != null ? rangeToPrice(range, v) : p.preco_promo
      if (preco == null) { erros.push(`${a.mlb}: sem preço`); continue }
      const r = await ctx.ativar(a, p, preco)
      if (r.ok) ok++; else erros.push(`${a.mlb}: HTTP ${r.http ?? 0} ${r.erro ?? JSON.stringify(r.resposta ?? '').slice(0, 80)}`)
      await new Promise((res) => setTimeout(res, 150))
    }
    setProg(`${ok} ok${semPost ? ` · ${semPost} pulada(s): ${MOTIVO_SEM_POST}` : ''}${erros.length ? ` · ${erros.length} recusadas: ${erros.join(' | ')}` : ''} — tudo em escritas_promo.log`)
  }

  /** Ela, 14/10/2026: "além do botão de participar das selecionadas, o botão sair das selecionadas, válido para
   *  qualquer promoção, cupom ou não". Alvo = promoção marcada que está ativa ou pendente no ML; o resto da seleção
   *  é ignorado com aviso. Relâmpago o ML não deixa remover (o serviço devolve `relampago`). Mesmo ctx.remover do
   *  botão SAIR de cada linha: relê o item antes do DELETE e "já fora" conta como feito. */
  const sairSelecionadas = async () => {
    const alvos: Array<{ a: Anuncio; p: Promocao }> = []
    let ignoradas = 0
    for (const a of conta.ads) for (const p of a.promocoes) {
      if (!ctx.sel.has(selKey(a, p))) continue
      if (p.status === 'started' || p.status === 'pending') alvos.push({ a, p }); else ignoradas++
    }
    const aviso = ignoradas ? ` (${ignoradas} marcada(s) não estão ativas nem pendentes — ignoradas)` : ''
    if (!alvos.length) { setProg(`Nenhuma promoção ativa ou pendente marcada${aviso}. Marque as caixinhas das promoções das quais quer sair e clique de novo.`); return }
    if (!ctx.escreve) {
      ctx.marcarLote(alvos.map(({ a, p }) => ({ k: selKey(a, p), status: null, preview: true })))
      setProg(`${alvos.length} saída(s) marcadas em preview (nada enviado ao ML)${aviso}`)
      return
    }
    if (!armadoSair) { armarSair(); return }
    desarmarSair()
    let ok = 0; let jaFora = 0; let relampago = 0; const erros: string[] = []
    for (let i = 0; i < alvos.length; i++) {
      const { a, p } = alvos[i]
      setProg(`saindo ${i + 1}/${alvos.length} — ${a.mlb}…`)
      const r = await ctx.remover(a, p)
      if (r.ok && r.ja_fora) jaFora++
      else if (r.ok) ok++
      else if (r.relampago) relampago++
      else erros.push(`${a.mlb}: HTTP ${r.http ?? 0} ${(r.erro ?? JSON.stringify(r.resposta ?? '')).slice(0, 120)}`)
      await new Promise((res) => setTimeout(res, 150))
    }
    setProg(`${ok} saíram${jaFora ? ` · ${jaFora} já estavam fora (nada enviado)` : ''}${relampago ? ` · ${relampago} relâmpago(s) não se removem (regra do ML)` : ''}${erros.length ? ` · ${erros.length} recusadas: ${erros.join(' | ')}` : ''}${aviso} — tudo em escritas_promo.log`)
  }

  return (
    <Stack spacing={2}>
      <Panel title={<Stack direction="row" justifyContent="space-between" alignItems="center"><span>Campanhas disponíveis</span>{campSel ? <Button size="small" onClick={() => { setCampSel(null); setPage(1) }}>limpar seleção ✕</Button> : null}</Stack>}
        count={<span title="Contado no status de agora de cada promoção (relido da API 3× ao dia e a cada rodada do robô) — a mesma fonte da tabela e dos contadores abaixo.">{cards.length} em curso{encerradas.length ? <> · <Box component="button" type="button" onClick={() => setVerEncerradas((v) => !v)} sx={{ all: 'unset', cursor: 'pointer', textDecoration: 'underline', color: 'text.secondary' }}>{verEncerradas ? 'esconder' : 'mostrar'} {encerradas.length} encerrada{encerradas.length !== 1 ? 's' : ''}</Box></> : null}</span>}>
        <P>Clique num card para ver o preço e a M.C. dessa campanha específica na tabela abaixo — igual ao Mercado Turbo. Sem seleção, a tabela mostra a melhor promoção simulada de cada anúncio. Clique na linha de um anúncio (↗) para abrir <b>todas</b> as campanhas dele na aba "Por anúncio". Os números dos cards são do <b>status atual</b> (API relida {conta.status_atualizado_em ? `às ${new Date(conta.status_atualizado_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}` : 'só na coleta completa'}); campanha com data final passada sai daqui sozinha.</P>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 1.25 }}>
          {cards.map((c) => {
            const sel = campSel === c.key
            const per = c.start_date && c.finish_date ? `${ddmm(c.start_date)} a ${ddmm(c.finish_date)}${dias(c.start_date, c.finish_date) ? ` · ${dias(c.start_date, c.finish_date)} dias` : ''}` : 'Período indeterminado'
            return (
              <Box key={c.key} component="button" type="button" onClick={() => { setCampSel(sel ? null : c.key); setPage(1) }}
                sx={{ textAlign: 'left', borderRadius: 2, p: '12px 14px', cursor: 'pointer', fontFamily: 'inherit', border: '1px solid transparent',
                  bgcolor: sel ? BRAND : 'action.hover', color: sel ? '#fff' : 'text.primary', '&:hover': { borderColor: sel ? BRAND : 'text.disabled' } }}>
                <Typography noWrap sx={{ fontWeight: 600, fontSize: 14, color: 'inherit' }}>{c.name}</Typography>
                <Typography sx={{ fontSize: 12, color: sel ? 'rgba(255,255,255,.75)' : 'text.disabled', mb: 1 }}>{c.type_label}{c.encerrada ? <Box component="span" sx={{ ml: 0.75, color: sel ? '#fff' : 'error.main', fontWeight: 600 }} title="A data final já passou. Se ainda há promoções 'ativas', é o ML que ainda não encerrou — a próxima releitura confirma.">· ENCERRADA{c.n_ativa ? ` (ML ainda devolve ${c.n_ativa} ativa${c.n_ativa !== 1 ? 's' : ''})` : ''}</Box> : null}</Typography>
                <Typography sx={{ fontSize: 12, color: sel ? 'rgba(255,255,255,.85)' : 'text.secondary' }}>{per}</Typography>
                <Typography sx={{ fontSize: 11.5, color: sel ? 'rgba(255,255,255,.75)' : 'text.disabled', mt: 0.75 }}>
                  <Box component="b" sx={{ color: sel ? '#fff' : 'success.main' }}>{c.n_ativa} ativa{c.n_ativa !== 1 ? 's' : ''}</Box> · {c.n_candidata} candidata{c.n_candidata !== 1 ? 's' : ''} · {c.n_ads} de {conta.ads.length} anúncios
                </Typography>
              </Box>
            )
          })}
        </Box>
      </Panel>

      <Panel>
        <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setBulk(bulk == null ? textoSelecao(conta, ctx.sel, ctx.pickerVal, ctx.base, ctx.cmvManual) : null)}>▾ Ações Múltiplas</Button>
          <Button variant={armado ? 'contained' : 'outlined'} color={armado ? 'error' : 'success'} size="small" onClick={() => void participarSelecionadas()} sx={{ borderStyle: ctx.escreve ? 'solid' : 'dashed', fontWeight: armado ? 700 : 500 }}>
            {armado ? `⚠ Confirmar: ${ctx.sel.size} chamada(s) reais à API` : `▶ Participar nas selecionadas${ctx.escreve ? '' : ' (preview)'}`}
          </Button>
          <Button variant={armadoSair ? 'contained' : 'outlined'} color="error" size="small" onClick={() => void sairSelecionadas()} sx={{ borderStyle: ctx.escreve ? 'solid' : 'dashed', fontWeight: armadoSair ? 700 : 500 }}
            title="Sai das promoções marcadas que estão ativas ou pendentes no ML (cupom também). Relâmpago não se remove. Dois cliques para confirmar.">
            {armadoSair ? '⚠ Confirmar saída: DELETE real na API' : `🔻 Sair das selecionadas${ctx.escreve ? '' : ' (preview)'}`}
          </Button>
          <Button size="small" color="inherit" onClick={() => { ctx.limparSel(); setBulk(null); setProg(null) }}>Limpar seleção</Button>
          <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>{ctx.sel.size} selecionada(s)</Typography>
          {prog ? <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{prog}</Typography> : null}
          <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }} flexWrap="wrap" useFlexGap>
            {STATUS.map(([k, l]) => {
              const on = status === k
              return (
                <Box key={k} component="button" type="button" onClick={() => { setStatus(k); setPage(1) }}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 13, cursor: 'pointer', borderRadius: 99, px: 1.6, py: 0.7, fontFamily: 'inherit',
                    border: `1px solid ${on ? BRAND : t.palette.divider}`, bgcolor: on ? `${BRAND}14` : 'background.paper', color: on ? BRAND : 'text.secondary', fontWeight: on ? 600 : 400 }}>
                  {l} <Box component="span" sx={{ fontSize: 11, color: on ? BRAND : 'text.disabled' }}>{counts[k]}</Box>
                </Box>
              )
            })}
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
          <TextField size="small" fullWidth placeholder="Buscar por título, MLB ou SKU…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1) }} sx={{ maxWidth: 520 }} />
          <Typography sx={{ fontSize: 13, color: 'text.disabled', ml: 'auto', whiteSpace: 'nowrap' }}>{visiveis.length} de {linhas.length} anúncios{linhas.length !== conta.ads.length ? ` (${conta.ads.length} no total)` : ''}</Typography>
        </Stack>
        {bulk != null ? (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.05em', mb: 0.5 }}>Selecione tudo (Ctrl+A) e copie (Ctrl+C) — para colar no chat</Typography>
            <TextField multiline minRows={4} fullWidth value={bulk} slotProps={{ htmlInput: { readOnly: true, style: { fontFamily: '"IBM Plex Mono", monospace', fontSize: 12.5 } } }} />
          </Box>
        ) : null}

        <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Table size="small" sx={{ minWidth: 1000, '& th': { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', whiteSpace: 'nowrap', bgcolor: 'action.hover' }, '& td': { whiteSpace: 'nowrap', verticalAlign: 'middle' } }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox"><Checkbox size="small" checked={visiveis.length > 0 && todosPagina} onChange={(e) => marcarPagina(e.target.checked)} title="Selecionar a página" /></TableCell>
                <TableCell>Anúncio</TableCell>
                <TableCell align="right" sx={thEstreito}>Preço cheio + M.C. {ctx.base === 'real' ? 'oficial' : 'ajustada'} no preço cheio<br /><Box component="span" sx={{ textTransform: 'none', letterSpacing: 0 }}>(antes da promo) e M.C. hoje (promoção ativa)</Box></TableCell>
                <TableCell align="right">Eu banco (vendedor)</TableCell>
                <TableCell align="right">ML banca</TableCell>
                <TableCell align="right">Desconto total</TableCell>
                <TableCell align="right">Preço final{camp ? <><br /><Box component="span" sx={{ textTransform: 'none', letterSpacing: 0 }}>{camp.name}</Box></> : null}</TableCell>
                <TableCell align="right" sx={{ ...thEstreito, bgcolor: `${t.palette.primary.main}1a !important` }}>M.C. {ctx.base === 'real' ? 'oficial' : 'ajustada'} com a promoção<br /><Box component="span" sx={{ textTransform: 'none', letterSpacing: 0 }}>preço cheio − desconto desta campanha</Box></TableCell>
                <TableCell align="right">Pedidos 30d</TableCell>
                <TableCell sx={{ position: 'sticky', right: 0, zIndex: 2 }}>Ação <SeloAcao /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visiveis.map((a) => {
                const p = promoParaExibir(a)
                const k = p ? selKey(a, p) : ''
                const m = p ? mcDe(a, p, ctx.base, ctx.cmvManual) : null
                const local = p ? ctx.statusLocal.get(k) : undefined
                return (
                  <TableRow key={a.mlb} hover sx={{ cursor: 'pointer', bgcolor: m?.faltas.length ? `${t.palette.warning.main}12` : undefined }} onClick={(e) => { if ((e.target as HTMLElement).closest('input,button,a')) return; ctx.abrirAnuncio(a.mlb) }}>
                    <TableCell padding="checkbox">{p ? <Checkbox size="small" checked={ctx.sel.has(k)} onChange={(e) => ctx.toggleSel(k, e.target.checked)} /> : null}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal !important' }}><AdCell a={a} caret extra={local?.preview ? <Pill label="Ativa (preview)" tone="mock" /> : null} /></TableCell>
                    <TableCell align="right"><OrigCell a={a} /></TableCell>
                    <TableCell align="right">{p ? <SplitCell rs={p.seller_rs} pc={p.seller_pct} p={p} /> : '—'}</TableCell>
                    <TableCell align="right">{p ? <SplitCell rs={p.ml_rs} pc={p.ml_pct} p={p} /> : '—'}</TableCell>
                    <TableCell align="right">{p ? <SplitCell rs={p.desc_total_rs} pc={p.desc_total_pct} p={{ ...p, split_origem: '' }} /> : '—'}</TableCell>
                    <TableCell align="right"><PrecoPromoCell p={p} /></TableCell>
                    <TableCell align="right" sx={{ bgcolor: `${t.palette.primary.main}0a` }}><McFinalCell a={a} p={p} /></TableCell>
                    <TableCell align="right" sx={{ color: 'text.disabled' }}>{a.pedidos_30d}</TableCell>
                    <TableCell sx={{ position: 'sticky', right: 0, bgcolor: 'background.paper', boxShadow: '-9px 0 9px -9px rgba(0,0,0,.2)' }}><AcaoCell a={a} p={p} /></TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>
        {visiveis.length < linhas.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.75 }}>
            <Button variant="outlined" color="inherit" onClick={() => setPage(page + 1)}>+ Carregar mais páginas <Box component="span" sx={{ color: 'text.disabled', ml: 0.75 }}>({linhas.length - visiveis.length} restantes)</Box></Button>
          </Box>
        ) : null}
      </Panel>
    </Stack>
  )
}
